import { CustomerBillingModel, CustomerModel } from 'api-hooks/customer';
import { Option } from 'baseui/select';
import { toTitleCase, transformLabel } from 'common/utils/string';
import { Input } from 'components/elements';
import { useFormState } from 'components/elements/form';
import Text from 'components/elements/text';
import useDialog from 'hooks/use-dialog';
import CustomerSelectInput from 'legacy-modules/master/customer/components/select-input';
import EmployeeSelectInput from 'legacy-modules/master/select-input/employee-select-input';
import CustomerBillingSelectInput from 'legacy-modules/sales/component/customer-billing-select-input';
import CustomerSalesWarningToast from 'legacy-modules/sales/component/customer-sales-warning-toast';
import TransactionAt from 'legacy-modules/sales/component/transaction-at';
import NoticeSuspendDialog from 'legacy-modules/sales/notice-suspend-dialog';
import {
  CardSection,
  FullContainer,
  HalfContainer,
  OneThirdContainer,
  SectionContainer,
  SeparatorVertical,
} from 'legacy-modules/styles';
import useTranslation from 'next-translate/useTranslation';
import * as React from 'react';
import { useFormContext, useWatch } from 'react-hook-form';

import { ProductSaleFormType } from './form-type';

function transformer(item: CustomerBillingModel): Option {
  return {
    id: item.id,
    label: transformLabel([item.name, item.address, item.phone]),
  };
}

interface SalesDetailFieldProps {}

export default function SalesDetailField(props: SalesDetailFieldProps) {
  const { t } = useTranslation();

  const { control, setValue, getValues } =
    useFormContext<ProductSaleFormType>();
  const isEdit = !!getValues('productSale');
  const { editable } = useFormState();
  const dialog = useDialog();

  const [customerId, total, remainingTransactionQuota] = useWatch({
    control,
    name: ['customerId', 'total', 'remainingTransactionQuota'],
  });

  const showLimitTransactionDialog = React.useCallback(() => {
    dialog.showCustom({
      render: (close) => <NoticeSuspendDialog onClose={close} type="limited" />,
    });
  }, [dialog]);

  const isExceedingQuota =
    editable && remainingTransactionQuota && total > remainingTransactionQuota;

  React.useEffect(() => {
    if (isExceedingQuota) {
      showLimitTransactionDialog();
    }
    setValue('isExceedingQuota', isExceedingQuota || false);
  }, [setValue, showLimitTransactionDialog, isExceedingQuota]);

  React.useEffect(() => {
    if (!customerId || customerId === '') {
      setValue('customerBillingOptions', []);
      setValue('customerBillingId', '');
      setValue('address', '');
      setValue('phone', '');
      setValue('hasDueReceivable', false);
      setValue('customerStatus', '');
      setValue('remainingTransactionQuota', null);
      setValue('isExceedingQuota', false);
    }
  }, [customerId, setValue]);

  const onCustomerChangeDetail = React.useCallback(
    (customer: CustomerModel) => {
      const _options = customer.billings.map((billing) => transformer(billing));
      const remainingTransactionQuota =
        customer.system.remainingTransactionQuota;
      const isExceedingQuota =
        customer.system.isTransactionLimited &&
        total > remainingTransactionQuota;

      setValue('phone', customer.phone);
      setValue('address', toTitleCase(customer.address || ''));
      setValue('customerBillingOptions', _options);
      setValue('isExceedingQuota', isExceedingQuota);
      setValue('remainingTransactionQuota', remainingTransactionQuota || null);

      if (editable) {
        if (isExceedingQuota) showLimitTransactionDialog();

        setValue(
          'customerBillingId',
          _options.length ? (_options?.[0]?.id as string) || '' : '',
        );
        setValue('hasDueReceivable', customer.hasDueReceivables);
        setValue('customerStatus', customer.status);
      }
    },
    [editable, setValue, showLimitTransactionDialog, total],
  );

  return (
    <CardSection style={{ paddingBottom: 0 }}>
      <FullContainer>
        <Text variant="HeadingXSmall">{t('common:sales_details')}</Text>
        <SeparatorVertical $margin={36} />
        <SectionContainer>
          <HalfContainer
            $style={{
              display: 'flex',
              alignItems: 'flex-end',
              justifyContent: 'space-between',
            }}
          >
            <TransactionAt
              name="transactionAt"
              clearable={false}
              label={t('common:transaction_at')}
              placeholder={t('common:transaction_at')}
              hasTime
              required
            />
          </HalfContainer>
          {isEdit && (
            <HalfContainer>
              <Input
                type="text"
                name="number"
                label={t('common:invoice_number')}
                placeholder={t('common:invoice_number')}
                disabled
              />
            </HalfContainer>
          )}
        </SectionContainer>
        <SectionContainer>
          <OneThirdContainer>
            <EmployeeSelectInput
              name="adminId"
              placeholder={t('common:admin')}
              label={t('common:admin')}
              disabled
              required
            />
          </OneThirdContainer>
          <OneThirdContainer>
            <EmployeeSelectInput
              name="operatorId"
              label={t('common:operator')}
              placeholder={t('common:operator')}
              required
            />
          </OneThirdContainer>
          <OneThirdContainer>
            <EmployeeSelectInput
              name="salesmanId"
              label={t('common:sales')}
              placeholder={t('common:sales')}
            />
          </OneThirdContainer>
        </SectionContainer>
        <SectionContainer>
          <HalfContainer>
            <CustomerSelectInput
              name="customerId"
              required
              onAfterDetailChange={onCustomerChangeDetail}
              isShowTransactionQuota
            />
          </HalfContainer>
          <HalfContainer>
            <CustomerBillingSelectInput name="customerBillingId" />
          </HalfContainer>
        </SectionContainer>
        <SectionContainer>
          <HalfContainer>
            <Input
              type="text"
              name="phone"
              label={t('common:phone_number')}
              placeholder={t('common:phone_number')}
              disabled
            />
          </HalfContainer>
          <HalfContainer>
            <Input
              type="text"
              name="address"
              label={t('common:address')}
              placeholder={t('common:address')}
              disabled
              fontCase="TitleCase"
            />
          </HalfContainer>
        </SectionContainer>
      </FullContainer>
      <CustomerSalesWarningToast />
      <SeparatorVertical $margin={12} />
    </CardSection>
  );
}
